/**
 * /portal-activity — admin-side inbox showing a chronological feed of
 * client-driven events (comments filed, reports emailed out).
 *
 * Filters: window (1w/2w/1mo), event kind (all/comments/emails), client,
 * target kind. Clicking a row navigates to the matching main-app view.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, GhostButton, PillBadge } from "../components/Primitives";
import { SingleSelectDropdown } from "../components/SingleSelectDropdown";
import {
  MessageSquare, Mail, RefreshCw, Building2, AlertOctagon,
  Wrench, FileSignature, ArrowRight,
} from "lucide-react";

const WINDOW_OPTIONS = [
  { days: 7,  label: "1 week" },
  { days: 14, label: "2 weeks" },
  { days: 30, label: "1 month" },
];

const KIND_META = {
  incidents:   { label: "Incident",     icon: AlertOctagon,    path: "/incidents" },
  maintenance: { label: "Maintenance",  icon: Wrench,          path: "/maintenance" },
  dar:         { label: "DAR",          icon: FileSignature,   path: "/dar" },
  tour_scans:  { label: "Tour scan",    icon: FileSignature,   path: "/tour-scans" },
};

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch (err) { return iso; }
}

function relTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return "";
  const abs = Math.abs(diff) / 1000;
  if (abs < 60) return "just now";
  const units = [
    [60 * 60 * 24 * 7,  "w"],
    [60 * 60 * 24,      "d"],
    [60 * 60,           "h"],
    [60,                "m"],
  ];
  for (const [s, l] of units) {
    if (abs >= s) return `${Math.floor(abs / s)}${l} ago`;
  }
  return "just now";
}


export default function PortalActivity() {
  const nav = useNavigate();
  const [days, setDays] = useState(30);
  const [eventKind, setEventKind] = useState("all");
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => setClients([]));
  }, []);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const params = { days, event_kind: eventKind };
      if (clientId) params.client_id = clientId;
      const r = await api.get("/portal-activity", { params });
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(errorMessage(err));
      setRows([]);
    } finally { setLoading(false); setRefreshing(false); }
  }, [days, eventKind, clientId]);

  useEffect(() => { load(); }, [load]);

  const clientOpts = useMemo(() => [
    { value: "", label: "All clients" },
    ...clients.map((c) => ({ value: c.id, label: c.company_name || c.name || "—" })),
  ], [clients]);

  const commentCount = rows.filter((r) => r.event_kind === "comment").length;
  const emailCount   = rows.filter((r) => r.event_kind === "email_forward").length;

  const goToTarget = (r) => {
    const meta = KIND_META[r.target_kind];
    if (meta?.path) nav(meta.path);
  };

  return (
    <div data-testid="portal-activity-page">
      <PageHeader
        title="Portal Activity"
        subtitle="Real-time feed of client-driven events across your agency — comments left on reports, PDFs emailed out, and more."
        action={
          <GhostButton onClick={() => load(true)} disabled={refreshing} data-testid="portal-activity-refresh">
            <RefreshCw className={`size-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </GhostButton>
        }
      />

      <div className="p-8 space-y-5">
        {/* Roll-up strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Window</div>
            <div className="font-display text-lg font-black text-white mt-1">Last {days} days</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Total events</div>
            <div className="font-display text-2xl font-black text-white mt-1" data-testid="portal-activity-total">{rows.length}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Client comments</div>
            <div className="font-display text-2xl font-black text-[#FF9F0A] mt-1" data-testid="portal-activity-comments-count">{commentCount}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Reports emailed</div>
            <div className="font-display text-2xl font-black text-[#0A84FF] mt-1" data-testid="portal-activity-emails-count">{emailCount}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3" data-testid="portal-activity-filters">
          <div className="min-w-[220px]">
            <div className="label-mute mb-1">Client</div>
            <SingleSelectDropdown
              testId="portal-activity-client-filter"
              options={clientOpts}
              value={clientId}
              onChange={setClientId}
              triggerClassName="min-w-[220px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Event kind</div>
            <div className="inline-flex items-center gap-2">
              {[
                { v: "all",           label: "All" },
                { v: "comment",       label: "Comments" },
                { v: "email_forward", label: "Email forwards" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  data-testid={`portal-activity-kind-${opt.v}`}
                  onClick={() => setEventKind(opt.v)}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border transition-colors ${
                    eventKind === opt.v
                      ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10"
                      : "border-[#27272A] text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-mute mb-1">Window</div>
            <div className="inline-flex items-center gap-2">
              {WINDOW_OPTIONS.map((opt) => (
                <button
                  key={opt.days}
                  data-testid={`portal-activity-window-${opt.days}d`}
                  onClick={() => setDays(opt.days)}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border transition-colors ${
                    days === opt.days
                      ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10"
                      : "border-[#27272A] text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ml-auto text-xs text-zinc-500 font-mono">
            {loading ? "Loading…" : `${rows.length} ${rows.length === 1 ? "event" : "events"}`}
          </div>
        </div>

        {/* Feed */}
        <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="portal-activity-feed">
          {loading ? (
            <div className="p-12 text-center text-zinc-500 text-sm scanlines">Loading activity…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-zinc-500 text-sm scanlines">
              No client activity in the last {days} days. When clients post comments or email a report, it&rsquo;ll show up here.
            </div>
          ) : (
            <div className="divide-y divide-[#1c1c1e]">
              {rows.map((r, idx) => {
                const meta = KIND_META[r.target_kind] || {};
                const KindIcon = meta.icon;
                const isComment = r.event_kind === "comment";
                const isAdminReply = isComment && r.user_role === "admin";
                return (
                  <div
                    key={r.comment_id || `${r.event_kind}-${idx}`}
                    className="px-4 py-3 flex items-start gap-3 hover:bg-[#141414] transition-colors"
                    data-testid={`portal-activity-row-${idx}`}
                  >
                    <div className={`size-8 shrink-0 border border-[#27272A] flex items-center justify-center ${isComment ? "text-[#FF9F0A]" : "text-[#0A84FF]"}`}>
                      {isComment ? <MessageSquare className="size-4" /> : <Mail className="size-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-zinc-100">
                          {isComment
                            ? (isAdminReply ? "Internal reply on " : "Client commented on ")
                            : "Report emailed · "}
                        </span>
                        {KindIcon && <KindIcon className="size-3.5 text-zinc-400" />}
                        <span className="text-sm text-zinc-200 truncate max-w-md">{r.target_title}</span>
                        {isAdminReply && <PillBadge tone="blue">Internal</PillBadge>}
                        {!isComment && r.sent === false && <PillBadge tone="red">delivery failed</PillBadge>}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                        <span className="inline-flex items-center gap-1"><Building2 className="size-3" /> {r.client_name}</span>
                        <span>{r.site_name}</span>
                        <span>by {r.user_name}</span>
                        <span>{fmtTime(r.when)} · {relTime(r.when)}</span>
                      </div>
                      {isComment && r.body && (
                        <div className="text-sm text-zinc-300 mt-2 border-l-2 border-[#27272A] pl-3 line-clamp-3 whitespace-pre-line">
                          {r.body}
                        </div>
                      )}
                      {!isComment && (
                        <div className="text-sm text-zinc-400 mt-2 flex flex-wrap gap-x-4 gap-y-0.5">
                          <span><span className="text-zinc-500">Subject:</span> {r.subject || "—"}</span>
                          <span><span className="text-zinc-500">To:</span> {(r.recipients || []).join(", ")}</span>
                          {r.reason && <span className="text-[#FF3B30]"><span className="text-zinc-500">Reason:</span> {r.reason}</span>}
                        </div>
                      )}
                    </div>
                    {meta.path && (
                      <button
                        type="button"
                        onClick={() => goToTarget(r)}
                        className="p-1.5 text-zinc-500 hover:text-[#FF3B30] transition-colors"
                        title={`Open ${meta.label}s`}
                        data-testid={`portal-activity-goto-${idx}`}
                      >
                        <ArrowRight className="size-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
